import { is_gen_2, isEM, isThreeEM } from 'src/helpers/interface_helpers';
//Types
import type { Device } from 'src/types/Device';
import type { ChartConfig } from 'src/types/Chart';

export const getSeriesAndUnitByComponent = (
	device: Device,
	component: string,
	extid = '',
	units: { [key: string]: string } = {},
	prevPeriodComparison: boolean,
): {
	series: ChartConfig['series'];
	unit: string;
} => {
	let series = [];
	let unit = '';
	switch (component) {
		case 'distance':
			series = [
				{
					name: 'min',
					style: `min_${component}`,
					visible: true,
				},
				{
					name: 'max',
					style: `max_${component}`,
					visible: true,
				},
				{
					name: 'avg',
					style: `avg_${component}`,
					visible: true,
				},
			];
			unit = 'mm';
			break;
		case 'temperature':
			series = [
				{
					name: `min_${component}${extid}`,
					style: 'min_temperature',
					visible: true,
				},
				{
					name: `max_${component}${extid}`,
					style: 'max_temperature',
					visible: true,
				},
			];
			unit = '°C';
			break;
		case 'humidity':
		case 'moisture':
			series = [
				{
					name: component,
					style: 'humidity',
					visible: true,
				},
			];
			unit = '%';
			break;
		case 'precipitation':
			series = [
				{
					name: component,
					style: 'humidity',
					visible: true,
				},
			];
			unit = 'mm';
			break;
		case 'pressure':
			series = [
				{
					name: `min_${component}`,
					style: `min_distance`,
					visible: true,
				},
				{
					name: `max_${component}`,
					style: `max_distance`,
					visible: true,
				},
			];
			unit = 'hPa';
			break;
		case 'illuminance':
			series = [
				{
					name: component,
					style: 'avg_distance',
					visible: true,
				},
			];
			unit = 'lx';
			break;
		case 'wind_speed':
			series = [
				{
					name: component,
					style: 'min_distance',
					visible: true,
				},
			];
			unit = 'm/s';
			break;
		case 'uv':
			series = [
				{
					name: component,
					style: 'max_temperature',
					visible: true,
				},
			];
			unit = '';
			break;
		case 'consumption':
			if (isThreeEM(device.type) && device?.mode != 'monophase') {
				series = [
					{
						visible: true,
						name: 'phase_a',
						stack: 'phase_a',
						style: 'phase_a',
						index: 0,
					},
					{
						visible: true,
						name: 'phase_b',
						stack: 'phase_b',
						style: 'phase_b',
						index: 1,
					},
					{
						visible: true,
						name: 'phase_c',
						stack: 'phase_c',
						style: 'phase_c',
						index: 2,
					},
					{
						visible: true,
						name: 'total',
						stack: 'total',
						style: 'total_consumption',
						index: 'sum',
					},
					{
						visible: true,
						name: 'phase_a_returned',
						stack: 'phase_a',
						style: 'phase_a_returned',
						index: 0,
					},
					{
						visible: true,
						name: 'phase_b_returned',
						stack: 'phase_b',
						style: 'phase_b_returned',
						index: 1,
					},
					{
						visible: true,
						name: 'phase_c_returned',
						stack: 'phase_c',
						style: 'phase_c_returned',
						index: 2,
					},
					{
						visible: true,
						name: 'total_returned',
						stack: 'total',
						style: 'total_returned',
						index: 'sum',
					},
				];
			} else if (
				isEM(device.type) ||
				device.category == 'pv' ||
				(is_gen_2(device) && device.category != 'roller')
			) {
				series = [
					{
						visible: true,
						name: component,
						stack: 'consumption',
						style: 'consumption',
					},
					{
						visible: true,
						name: 'reversed',
						stack: 'consumption',
						style: 'reversed',
					},
					{
						visible: prevPeriodComparison,
						prevPeriod: true,
						name: component,
						stack: 'prevPeriod',
						style: 'prev_period_consumption',
					},
					{
						visible: prevPeriodComparison,
						prevPeriod: true,
						name: 'reversed',
						stack: 'prevPeriod',
						style: 'prev_period_reversed',
					},
				];
			} else {
				series = [
					{
						name: component,
						style: 'consumption',
						visible: true,
					},
					{
						visible: prevPeriodComparison,
						style: 'prev_period_consumption',
						prevPeriod: true,
						name: component,
					},
				];
			}
			unit = 'Wh';
			break;
		case 'voltage':
			if (isThreeEM(device.type)) {
				series = [
					{
						visible: true,
						name: `phase_a_min_${component}`,
						style: 'phase_a_min_voltage',
						index: 0,
					},
					{
						visible: true,
						name: `phase_a_max_${component}`,
						style: 'phase_a_max_voltage',
						index: 0,
					},
					{
						visible: true,
						name: `phase_b_min_${component}`,
						style: 'phase_b_min_voltage',
						index: 1,
					},
					{
						visible: true,
						name: `phase_b_max_${component}`,
						style: 'phase_b_max_voltage',
						index: 1,
					},
					{
						visible: true,
						name: `phase_c_min_${component}`,
						style: 'phase_c_min_voltage',
						index: 2,
					},
					{
						visible: true,
						name: `phase_c_max_${component}`,
						style: 'phase_c_max_voltage',
						index: 2,
					},
				];
				if (device?.mode == 'monophase') {
					series = [
						{
							name: `min_${component}`,
							style: 'min_voltage',
							visible: true,
						},
						{
							name: `max_${component}`,
							style: 'max_voltage',
							visible: true,
						},
					];
				}
			} else if (isEM(device.type)) {
				series = [
					{
						name: `min_${component}`,
						style: 'min_voltage',
						visible: true,
					},
					{
						name: `max_${component}`,
						style: 'max_voltage',
						visible: true,
					},
				];
			} else {
				series = [
					{
						name: component,
						style: 'voltage',
						visible: true,
					},
				];
			}
			unit = 'V';
			break;
		case 'voltmeter':
			series = [
				{
					name: `min_voltage${extid}`,
					style: 'min_voltage',
					visible: true,
				},
				{
					name: `max_voltage${extid}`,
					style: 'max_voltage',
					visible: true,
				},
			];
			unit = units?.xunit || 'V';
			break;
		case 'input':
			series = [
				{
					name: `min_percent${extid}`,
					style: 'min_input',
					visible: true,
				},
				{
					name: `max_percent${extid}`,
					style: 'max_input',
					visible: true,
				},
			];
			unit = units?.xunit || '%';
			break;
		case 'valve_position':
			series = [
				{
					name: 'position',
					style: 'valve_position',
					visible: true,
				},
			];
			unit = '%';
			// chart.height = 150;
			break;
		case 'ppm':
			series = [
				{
					name: component,
					style: 'ppm',
					visible: true,
				},
			];
			unit = 'ppm';
			break;
		case 'accumulation':
		case 'counter_accumulation':
			series = [
				{
					name: 'value',
					style: 'counter_accumulation',
					visible: true,
				},
			];
			unit = units[component] || '';
			break;
		case 'measurement':
		case 'freq_measurement':
			series = [
				{
					name: 'min',
					style: /^°(C|F)$/.test(units[component])
						? 'min_temperature'
						: 'freq_measurement_min',
					visible: true,
				},
				{
					name: 'max',
					style: /^°(C|F)$/.test(units[component])
						? 'max_temperature'
						: 'freq_measurement_max',
					visible: true,
				},
				{
					name: 'avg',
					style: 'freq_measurement_avg',
					visible: true,
				},
			];
			unit = units[component] || '';
			break;
		case 'counter':
			series = [
				{
					name: 'counter',
					style: 'counter_accumulation',
					visible: true,
				},
				{
					name: 'change',
					style: 'freq_measurement_avg',
					visible: true,
				},
			];
			unit = units[component] || '';
			break;
	}
	return { series, unit };
};
